/**
 * Componente de Formulário Rápido para Templates
 * 
 * Renderiza um formulário isolado baseado em um template específico,
 * reutilizando a lógica de criação de demandas do sistema.
 */

import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from '../../../components/Toast'
import { ArrowLeft, Send, Loader2 } from 'lucide-react'
import { TemplateFormRenderer } from '../components/TemplateFormRenderer'
import { validateTemplateFormData } from './schema'
import { demandaService } from '../../../services/demanda'
import { processoService } from '../../../services/processo'
import { authService } from '../../../services/auth'
import type { TemplateSchema } from '../types'
import type { Processo, CreateDemandaRequest } from '../../../models'

interface TemplateQuickFormProps {
  templateId: string
  templateSchema: TemplateSchema
  processoVinculado?: number | undefined // ID do processo vinculado ao template
  onSuccess?: () => void
  onCancel?: () => void
}

interface UploadProgress {
  status: 'idle' | 'validating' | 'creating' | 'uploading' | 'success' | 'error'
  progress: number
  message: string
}

export const TemplateQuickForm: React.FC<TemplateQuickFormProps> = ({
  templateId: _templateId,
  templateSchema,
  processoVinculado,
  onSuccess,
  onCancel,
}) => {
  const navigate = useNavigate()
  
  const [formData, setFormData] = useState<Record<string, unknown>>({})
  const [processo, setProcesso] = useState<Processo | null>(null)
  const [resolvedProcessoId, setResolvedProcessoId] = useState<number | undefined>(processoVinculado)
  const [isLoadingProcesso, setIsLoadingProcesso] = useState(false)
  const [uploadProgress, setUploadProgress] = useState<UploadProgress>({
    status: 'idle',
    progress: 0,
    message: ''
  })

  // Obter usuário logado
  const usuarioAtual = authService.getUser()

  // Sincronizar id resolvido quando prop mudar
  useEffect(() => {
    setResolvedProcessoId(processoVinculado)
  }, [processoVinculado])

  // Carregar processo vinculado e, se existir versão mais nova, usar a mais recente
  useEffect(() => {
    const loadProcesso = async () => {
      const targetId = resolvedProcessoId || processoVinculado
      if (!targetId) return

      try {
        setIsLoadingProcesso(true)
        const data = await processoService.getById(targetId)

        const versions = Array.isArray((data as any)?.versions) ? (data as any).versions as Array<{ id: number; active?: boolean }> : []
        const latestId = versions.length > 0
          ? Math.max(...versions.map(v => v.id))
          : data.id

        if (latestId !== targetId) {
          toast.info('Usando a versão mais recente do processo vinculado')
          setResolvedProcessoId(latestId)
          return
        }

        setProcesso(data as Processo)
      } catch (error) {
        console.error('Erro ao carregar processo:', error)
        toast.error('Erro ao carregar processo vinculado')
      } finally {
        setIsLoadingProcesso(false)
      }
    }

    loadProcesso()
  }, [processoVinculado, resolvedProcessoId])

  // Validar se há processo vinculado
  useEffect(() => {
    if (!processoVinculado && uploadProgress.status === 'idle') {
      toast.error('Este template precisa estar vinculado a um processo válido')
    }
  }, [processoVinculado, uploadProgress.status])

  const handleFormChange = (data: Record<string, unknown>) => {
    setFormData(data)
  }

  const handleSubmit = async () => {
    const processoIdParaUso = resolvedProcessoId || processoVinculado

    if (!processoIdParaUso || !processo) {
      toast.error('Processo não encontrado. Configure um processo válido para este template.')
      return
    }

    // Validar se o usuário está logado
    if (!usuarioAtual || !usuarioAtual.id) {
      toast.error('Usuário não autenticado. Faça login novamente.')
      return
    }

    try {
      // Validação do formulário
      setUploadProgress({
        status: 'validating',
        progress: 10,
        message: 'Validando formulário...'
      })

      if (!formData || Object.keys(formData).length === 0) {
        toast.error('Preencha os campos obrigatórios do formulário')
        setUploadProgress({ status: 'idle', progress: 0, message: '' })
        return
      }

      const validationResult = validateTemplateFormData(templateSchema, formData)
      if (!validationResult.success) {
        const firstError = validationResult.errors[0] || 'Preencha os campos obrigatórios do formulário'
        toast.error(firstError)
        setUploadProgress({ status: 'idle', progress: 0, message: '' })
        return
      }

      const validFormData = validationResult.data

      // Preparar dados da demanda usando configurações do fluxo simplificado
      const fluxo = templateSchema.fluxoSimplificado

      setUploadProgress({
        status: 'creating',
        progress: 30,
        message: 'Criando demanda...'
      })

      // Calcular data de entrega baseada nos dias úteis padrão
      let dataEntrega: string | undefined
      if (fluxo?.diasEntregaPadrao) {
        const hoje = new Date()
        const diasUteis = fluxo.diasEntregaPadrao
        let contador = 0
        const dataAtual = new Date(hoje)

        while (contador < diasUteis) {
          dataAtual.setDate(dataAtual.getDate() + 1)
          const diaSemana = dataAtual.getDay()
          if (diaSemana !== 0 && diaSemana !== 6) {
            contador++
          }
        }

        dataEntrega = dataAtual.toISOString().split('T')[0]
      }

      // Gerar título: usa o nome do processo; fallback para o título do template
      const titulo = processo.nome_processo || templateSchema.schema.title || 'Solicitação'

      // Achatar template_respostas para notação de colchetes (compatível com Laravel + FormData)
      const templateRespostasPayload: Record<string, string> = {
        'template_respostas[template_id]': templateSchema.template_id,
        'template_respostas[template_versao]': templateSchema.versao,
      }

      Object.entries(validFormData).forEach(([key, value]) => {
        if (value === undefined || value === null) return
        templateRespostasPayload[`template_respostas[respostas][${key}]`] = String(value)
      })

      // Montar payload da demanda (campos simples)
      const demandaPayload: Record<string, unknown> = {
        processo_fk: [processoIdParaUso],
        demandante_fk: usuarioAtual.id,
        titulo,
        prioridade: fluxo?.prioridadePadrao || 'Media',
        descricao: JSON.stringify(validFormData),
        sugestao_entrega: dataEntrega || new Date().toISOString().split('T')[0],
        observadores: [],
      }

      // Injetar campos flatten de template_respostas
      Object.assign(demandaPayload, templateRespostasPayload)

      setUploadProgress({
        status: 'uploading',
        progress: 70,
        message: 'Enviando demanda...'
      })

      // Criar demanda
      await demandaService.create(demandaPayload as unknown as CreateDemandaRequest)

      setUploadProgress({
        status: 'success',
        progress: 100,
        message: 'Demanda criada com sucesso!'
      })

      toast.success('Demanda criada com sucesso!')

      // Aguardar um pouco para mostrar o feedback
      setTimeout(() => {
        if (onSuccess) {
          onSuccess()
          return
        }

        // Direciona para a lista de demandas enviadas após criar
        navigate('/demands/sent')
      }, 1000)

    } catch (error: unknown) {
      console.error('Erro ao criar demanda:', error)
      
      setUploadProgress({
        status: 'error',
        progress: 0,
        message: 'Erro ao criar demanda'
      })

      const errorMessage = 
        (error && typeof error === 'object' && 'response' in error) 
          ? ((error as { response?: { data?: { message?: string } } }).response?.data?.message) 
          : 'Erro ao criar demanda. Tente novamente.'
      toast.error(errorMessage || 'Erro ao criar demanda. Tente novamente.')
    }
  }

  const handleCancel = () => {
    if (onCancel) {
      onCancel()
    } else {
      navigate(-1)
    }
  }

  const isSubmitting = uploadProgress.status !== 'idle' && uploadProgress.status !== 'error'
  const canSubmit = !isSubmitting && (resolvedProcessoId || processoVinculado) && processo

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={handleCancel}
            className="flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 transition-colors mb-4"
            disabled={isSubmitting}
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Voltar</span>
          </button>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 border border-gray-200 dark:border-gray-700">
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
              {templateSchema.schema.title || 'Formulário de Solicitação'}
            </h1>
            
            {templateSchema.schema.description && (
              <p className="text-gray-600 dark:text-gray-400">
                {templateSchema.schema.description}
              </p>
            )}

            {/* {processo && (
              <div className="mt-4 flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                <span className="font-medium">Processo:</span>
                <span className="px-2 py-1 bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 rounded">
                  {processo.nome_processo}
                </span>
              </div>
            )} */}
          </div>
        </div>

        {/* Progress Indicator */}
        {uploadProgress.status !== 'idle' && (
          <div className="mb-6 bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 border border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-3 mb-3">
              {uploadProgress.status === 'success' ? (
                <div className="w-8 h-8 rounded-full bg-green-100 dark:bg-green-900 flex items-center justify-center">
                  <svg className="w-5 h-5 text-green-600 dark:text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
              ) : uploadProgress.status === 'error' ? (
                <div className="w-8 h-8 rounded-full bg-red-100 dark:bg-red-900 flex items-center justify-center">
                  <svg className="w-5 h-5 text-red-600 dark:text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </div>
              ) : (
                <Loader2 className="w-8 h-8 text-blue-600 dark:text-blue-400 animate-spin" />
              )}
              
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-900 dark:text-white">
                  {uploadProgress.message}
                </p>
                <div className="mt-2 w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                  <div 
                    className={`h-2 rounded-full transition-all duration-300 ${
                      uploadProgress.status === 'success' ? 'bg-green-500' :
                      uploadProgress.status === 'error' ? 'bg-red-500' :
                      'bg-blue-500'
                    }`}
                    style={{ width: `${uploadProgress.progress}%` }}
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Form */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 border border-gray-200 dark:border-gray-700">
          {isLoadingProcesso ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 text-blue-600 dark:text-blue-400 animate-spin" />
            </div>
          ) : !processoVinculado ? (
            <div className="text-center py-12">
              <p className="text-gray-600 dark:text-gray-400">
                Este template precisa estar vinculado a um processo válido.
              </p>
              <button
                onClick={() => navigate('/gestao/processos')}
                className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Ir para Processos
              </button>
            </div>
          ) : (
            <>
              <TemplateFormRenderer
                templateSchema={templateSchema}
                formData={formData}
                onChange={handleFormChange}
              />

              {/* Actions */}
              <div className="mt-8 flex items-center justify-end gap-4 pt-6 border-t border-gray-200 dark:border-gray-700">
                <button
                  type="button"
                  onClick={handleCancel}
                  disabled={isSubmitting}
                  className="px-6 py-2.5 text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancelar
                </button>
                
                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={!canSubmit}
                  className="px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      <span>Enviando...</span>
                    </>
                  ) : (
                    <>
                      <Send className="w-5 h-5" />
                      <span>Enviar Solicitação</span>
                    </>
                  )}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
